import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Button, Chip, Avatar, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControlLabel, Switch, Alert, MenuItem, List, ListItem, ListItemText, Divider, Stack,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon, Category as CategoryIcon, ArrowUpward as UpIcon, ArrowDownward as DownIcon } from '@mui/icons-material';
import { gifticonAPI, gifticonCategoryAPI } from '../services/api';
import { GifticonProduct } from '../types';

const fmt = (n: number) => new Intl.NumberFormat('ko-KR').format(n);

interface FormState {
  goodsCode: string; brand: string; name: string; category: string; imageUrl: string;
  pointPrice: string; marketPrice: string; stock: string; sortOrder: string; visible: boolean;
}
const emptyForm: FormState = { goodsCode: '', brand: '', name: '', category: '', imageUrl: '', pointPrice: '', marketPrice: '', stock: '-1', sortOrder: '0', visible: true };

const Gifticons: React.FC = () => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<GifticonProduct | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [lookupError, setLookupError] = useState('');
  const [catOpen, setCatOpen] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [filterCat, setFilterCat] = useState('전체');

  const { data: items } = useQuery({
    queryKey: ['gifticons'],
    queryFn: async () => (await gifticonAPI.getAll()).data || [],
  });

  // 카테고리 (관리자 관리형)
  const { data: categories } = useQuery({
    queryKey: ['gifticon-categories'],
    queryFn: async () => (await gifticonCategoryAPI.getAll()).data || [],
  });
  const catNames = (categories || []).map((c) => c.name);

  // 카테고리 필터(클라이언트) - '전체'면 모두 표시
  const visibleItems = items?.filter((g) => filterCat === '전체' || (g.category || '기타') === filterCat);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['gifticons'] });
  const invalidateCats = () => queryClient.invalidateQueries({ queryKey: ['gifticon-categories'] });
  const createMutation = useMutation({ mutationFn: gifticonAPI.create, onSuccess: () => { invalidate(); handleClose(); } });
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<GifticonProduct> }) => gifticonAPI.update(id, data),
    onSuccess: () => { invalidate(); handleClose(); },
  });
  const deleteMutation = useMutation({ mutationFn: gifticonAPI.delete, onSuccess: invalidate });
  const lookupMutation = useMutation({
    mutationFn: (goodsCode: string) => gifticonAPI.lookup(goodsCode),
    onSuccess: (res) => {
      const g = res.data;
      if (g) setForm((p) => ({ ...p, brand: g.brandName || p.brand, name: g.goodsName || p.name, imageUrl: g.imageUrl || p.imageUrl, marketPrice: g.marketPrice ? String(g.marketPrice) : p.marketPrice }));
      setLookupError('');
    },
    onError: (e: any) => setLookupError(e?.response?.data?.error || 'Giftishow 조회 실패'),
  });

  // 카테고리 CRUD
  const createCatMutation = useMutation({
    mutationFn: (name: string) => gifticonCategoryAPI.create({ name }), // sortOrder는 서버가 맨 뒤로 자동 부여
    onSuccess: () => { invalidateCats(); setNewCat(''); },
  });
  const deleteCatMutation = useMutation({
    mutationFn: (id: number) => gifticonCategoryAPI.delete(id),
    onSuccess: invalidateCats,
  });
  // 순서 이동: 인접 카테고리와 sortOrder 교환
  const reorderCatMutation = useMutation({
    mutationFn: async ({ index, dir }: { index: number; dir: -1 | 1 }) => {
      const arr = categories || [];
      const j = index + dir;
      if (j < 0 || j >= arr.length) return;
      const a = arr[index], b = arr[j];
      // sortOrder가 같은(레거시 충돌) 경우 대비: 위치 기반으로 명확히 재부여
      const aSort = a.sortOrder === b.sortOrder ? index : a.sortOrder;
      const bSort = a.sortOrder === b.sortOrder ? j : b.sortOrder;
      await gifticonCategoryAPI.update(a.id, { sortOrder: bSort });
      await gifticonCategoryAPI.update(b.id, { sortOrder: aSort });
    },
    onSuccess: invalidateCats,
  });

  const handleOpen = (g?: GifticonProduct) => {
    setLookupError('');
    if (g) {
      setEditing(g);
      setForm({ goodsCode: g.goodsCode, brand: g.brand, name: g.name, category: g.category || '', imageUrl: g.imageUrl || '', pointPrice: String(g.pointPrice), marketPrice: g.marketPrice ? String(g.marketPrice) : '', stock: String(g.stock), sortOrder: String(g.sortOrder), visible: g.visible });
    } else {
      setEditing(null); setForm({ ...emptyForm, category: catNames[0] || '' });
    }
    setOpen(true);
  };
  const handleClose = () => { setOpen(false); setEditing(null); setForm(emptyForm); };

  const handleSubmit = () => {
    const data = {
      goodsCode: form.goodsCode, brand: form.brand, name: form.name, category: form.category, imageUrl: form.imageUrl || null,
      pointPrice: Number(form.pointPrice), marketPrice: form.marketPrice ? Number(form.marketPrice) : null,
      stock: Number(form.stock), sortOrder: Number(form.sortOrder), visible: form.visible,
    };
    if (editing) updateMutation.mutate({ id: editing.id, data });
    else createMutation.mutate(data);
  };

  const set = (k: keyof FormState) => (e: any) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">기프티콘 관리</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField select size="small" label="카테고리" value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="전체">전체 카테고리</MenuItem>
            {catNames.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <Button variant="outlined" startIcon={<CategoryIcon />} onClick={() => setCatOpen(true)}>카테고리 관리</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>기프티콘 추가</Button>
        </Stack>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>이미지</TableCell><TableCell>브랜드</TableCell><TableCell>상품명</TableCell>
              <TableCell>카테고리</TableCell><TableCell>goodsCode</TableCell><TableCell align="right">교환 포인트</TableCell>
              <TableCell align="right">재고</TableCell><TableCell>노출</TableCell><TableCell align="center">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleItems?.map((g) => (
              <TableRow key={g.id}>
                <TableCell><Avatar variant="rounded" src={g.imageUrl || undefined}>{g.brand?.[0]}</Avatar></TableCell>
                <TableCell>{g.brand}</TableCell>
                <TableCell>{g.name}</TableCell>
                <TableCell><Chip label={g.category || '기타'} size="small" variant="outlined" /></TableCell>
                <TableCell>{g.goodsCode}</TableCell>
                <TableCell align="right">{fmt(g.pointPrice)} P</TableCell>
                <TableCell align="right">{g.stock < 0 ? '무제한' : fmt(g.stock)}</TableCell>
                <TableCell><Chip label={g.visible && g.isActive ? '노출' : '숨김'} color={g.visible && g.isActive ? 'success' : 'default'} size="small" /></TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleOpen(g)}><EditIcon /></IconButton>
                  <IconButton size="small" color="error" onClick={() => window.confirm('삭제하시겠습니까?') && deleteMutation.mutate(g.id)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!visibleItems?.length && <TableRow><TableCell colSpan={9} align="center">{items?.length ? '해당 카테고리 기프티콘이 없습니다.' : '등록된 기프티콘이 없습니다.'}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 기프티콘 추가/수정 */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? '기프티콘 수정' : '기프티콘 추가'}</DialogTitle>
        <DialogContent>
          {lookupError && <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>{lookupError}</Alert>}
          <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2 }}>
            <TextField label="goodsCode" fullWidth value={form.goodsCode} onChange={set('goodsCode')} disabled={!!editing} />
            <Button variant="outlined" startIcon={<SearchIcon />} disabled={!form.goodsCode} onClick={() => lookupMutation.mutate(form.goodsCode)}>
              Giftishow 조회
            </Button>
          </Box>
          <TextField label="브랜드" fullWidth value={form.brand} onChange={set('brand')} sx={{ mb: 2 }} />
          <TextField label="상품명" fullWidth value={form.name} onChange={set('name')} sx={{ mb: 2 }} />
          <TextField select label="카테고리" fullWidth value={form.category} onChange={set('category')} sx={{ mb: 2 }}
            helperText={catNames.length ? undefined : '카테고리를 먼저 추가하세요.'}>
            {catNames.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField label="이미지 URL" fullWidth value={form.imageUrl} onChange={set('imageUrl')} sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField label="교환 포인트" type="number" fullWidth value={form.pointPrice} onChange={set('pointPrice')} />
            <TextField label="정가(참고)" type="number" fullWidth value={form.marketPrice} onChange={set('marketPrice')} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
            <TextField label="재고(-1 무제한)" type="number" fullWidth value={form.stock} onChange={set('stock')} />
            <TextField label="정렬순서" type="number" fullWidth value={form.sortOrder} onChange={set('sortOrder')} />
          </Box>
          <FormControlLabel control={<Switch checked={form.visible} onChange={(e) => setForm((p) => ({ ...p, visible: e.target.checked }))} />} label="앱 노출" />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>취소</Button>
          <Button variant="contained" disabled={!form.goodsCode || !form.brand || !form.name || !form.pointPrice || !form.category} onClick={handleSubmit}>
            {editing ? '수정' : '추가'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 카테고리 관리 */}
      <Dialog open={catOpen} onClose={() => setCatOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>카테고리 관리</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            앱 기프티콘 상점의 카테고리 탭입니다. 추가/삭제하면 앱에 바로 반영됩니다.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField size="small" fullWidth placeholder="새 카테고리명" value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && newCat.trim()) createCatMutation.mutate(newCat.trim()); }} />
            <Button variant="contained" disabled={!newCat.trim() || createCatMutation.isPending}
              onClick={() => createCatMutation.mutate(newCat.trim())}>추가</Button>
          </Box>
          {createCatMutation.isError && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {(createCatMutation.error as any)?.response?.data?.error || '추가 실패'}
            </Alert>
          )}
          <Divider />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            ▲▼ 로 순서를 바꾸면 앱 카테고리 탭 순서에 반영됩니다.
          </Typography>
          <List dense>
            {(categories || []).map((c, idx, arr) => (
              <ListItem key={c.id} secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  <IconButton edge="end" size="small" disabled={idx === 0 || reorderCatMutation.isPending}
                    onClick={() => reorderCatMutation.mutate({ index: idx, dir: -1 })}>
                    <UpIcon fontSize="small" />
                  </IconButton>
                  <IconButton edge="end" size="small" disabled={idx === arr.length - 1 || reorderCatMutation.isPending}
                    onClick={() => reorderCatMutation.mutate({ index: idx, dir: 1 })}>
                    <DownIcon fontSize="small" />
                  </IconButton>
                  <IconButton edge="end" size="small" color="error"
                    onClick={() => window.confirm(`'${c.name}' 카테고리를 삭제하시겠습니까?\n(해당 카테고리 기프티콘은 '기타'로 표시됩니다)`) && deleteCatMutation.mutate(c.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              }>
                <ListItemText primary={`${idx + 1}. ${c.name}`} />
              </ListItem>
            ))}
            {!categories?.length && <ListItem><ListItemText primary="카테고리가 없습니다." /></ListItem>}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Gifticons;
